import { lockScroll, unlockScroll } from '@theme/utilities';

(() => {
  if (window.__KervelanHeaderRuntimeLoaded) return;
  window.__KervelanHeaderRuntimeLoaded = true;

  const initKervelanHeader = (root) => {
    if (!root) return;

    root._kvStickyAbort?.abort();
    root.classList.add('kv-header-initializing');

    const controller = new AbortController();
    root._kvStickyAbort = controller;
    const { signal } = controller;

    const mode = root.dataset.stickyHeaderType || 'on-scroll-up';
    const header = root.querySelector('[data-kv-header]');
    const sentinel = root.querySelector('[data-kv-header-sentinel]');
    const spacer = root.querySelector('[data-kv-header-spacer]');

    if (!header || !sentinel || !spacer) {
      root.classList.remove('kv-header-initializing');
      return;
    }

    const mobileMenu = header.querySelector('[data-kv-mobile-menu]');
    const mobileSummary = mobileMenu?.querySelector(':scope > summary');
    const mobilePanel = header.querySelector('[data-kv-mobile-drawer]');
    const mobileClose = header.querySelector('[data-kv-mobile-menu-close]');
    const mobileBackdrop = header.querySelector('[data-kv-mobile-menu-backdrop]');

    const desktopMega = header.querySelector('[data-kv-shop-mega]');
    const desktopMegaParent = desktopMega?.querySelector('[data-kv-shop-mega-parent]');
    const desktopMegaMenu = desktopMega?.querySelector('[data-kv-shop-mega-menu]');
    const desktopMegaPanel = desktopMega?.querySelector('[data-kv-mega-panel]');
    const desktopMegaTriggers = Array.from(desktopMega?.querySelectorAll('[data-kv-mega-trigger]') || []);
    const desktopMegaGroups = Array.from(desktopMega?.querySelectorAll('[data-kv-mega-group]') || []);
    const desktopMegaGroupsByKey = new Map(
      desktopMegaGroups.map((group) => [group.dataset.kvMegaGroup, group])
    );
    const DEFAULT_DESKTOP_MEGA_GROUP = 'shop-all';

    let desktopMegaHoverTimer = 0;
    let desktopMegaCloseTimer = 0;
    const DESKTOP_MEGA_CLOSE_DELAY_MS = 180;

    const clearDesktopMegaCloseTimer = () => {
      window.clearTimeout(desktopMegaCloseTimer);
      desktopMegaCloseTimer = 0;
    };

    const clearDesktopMegaHoverTimer = () => {
      window.clearTimeout(desktopMegaHoverTimer);
      desktopMegaHoverTimer = 0;
    };

    const resetDesktopMega = () => {
      clearDesktopMegaHoverTimer();
      clearDesktopMegaCloseTimer();
      desktopMega?.classList.remove('is-hover-bridged');
      desktopMegaMenu?.classList.remove('is-tertiary-visible');
      desktopMegaMenu?.removeAttribute('data-active-group');
      desktopMegaPanel?.setAttribute('aria-hidden', 'true');

      desktopMegaTriggers.forEach((trigger) => {
        trigger.classList.remove('is-active');
        trigger.setAttribute('aria-expanded', 'false');
      });

      desktopMegaGroups.forEach((group) => {
        group.classList.remove('is-active', 'is-revealing');
        group.setAttribute('aria-hidden', 'true');
      });
    };

    const activateDesktopMegaGroup = (key) => {
      const group = desktopMegaGroupsByKey.get(key);
      if (!group || !desktopMegaMenu || !desktopMegaPanel) return;
      if (desktopMegaMenu.dataset.activeGroup === key) return;

      desktopMegaMenu.dataset.activeGroup = key;
      desktopMegaMenu.classList.add('is-tertiary-visible');
      desktopMegaPanel.setAttribute('aria-hidden', 'false');

      desktopMegaTriggers.forEach((trigger) => {
        const isActive = trigger.dataset.kvMegaTrigger === key;
        trigger.classList.toggle('is-active', isActive);
        trigger.setAttribute('aria-expanded', String(isActive));
      });

      desktopMegaGroups.forEach((candidate) => {
        const isActive = candidate === group;
        candidate.classList.toggle('is-active', isActive);
        candidate.classList.remove('is-revealing');
        candidate.setAttribute('aria-hidden', String(!isActive));
      });

      group.classList.add('is-revealing');
    };

    const scheduleDesktopMegaGroup = (key) => {
      clearDesktopMegaHoverTimer();
      desktopMegaHoverTimer = window.setTimeout(() => activateDesktopMegaGroup(key), 85);
    };

    if (desktopMega) {
      desktopMegaTriggers.forEach((trigger) => {
        trigger.addEventListener('pointerenter', () => scheduleDesktopMegaGroup(trigger.dataset.kvMegaTrigger), { signal });
        trigger.addEventListener('focus', () => activateDesktopMegaGroup(trigger.dataset.kvMegaTrigger), { signal });
        trigger.addEventListener('click', () => activateDesktopMegaGroup(trigger.dataset.kvMegaTrigger), { signal });
      });

      desktopMega.addEventListener('pointerenter', () => {
        clearDesktopMegaCloseTimer();
        desktopMega.classList.add('is-hover-bridged');
        desktopMegaParent?.setAttribute('aria-expanded', 'true');
        if (!desktopMegaMenu?.dataset.activeGroup) activateDesktopMegaGroup(DEFAULT_DESKTOP_MEGA_GROUP);
      }, { signal });

      desktopMega.addEventListener('pointerleave', () => {
        clearDesktopMegaCloseTimer();
        desktopMegaCloseTimer = window.setTimeout(() => {
          desktopMegaCloseTimer = 0;
          desktopMegaParent?.setAttribute('aria-expanded', 'false');
          resetDesktopMega();
        }, DESKTOP_MEGA_CLOSE_DELAY_MS);
      }, { signal });

      desktopMega.addEventListener('focusin', () => {
        clearDesktopMegaCloseTimer();
        desktopMega.classList.add('is-hover-bridged');
        desktopMegaParent?.setAttribute('aria-expanded', 'true');
        if (!desktopMegaMenu?.dataset.activeGroup) activateDesktopMegaGroup(DEFAULT_DESKTOP_MEGA_GROUP);
      }, { signal });

      desktopMega.addEventListener('focusout', (event) => {
        const next = event.relatedTarget;
        if (next instanceof Node && desktopMega.contains(next)) return;
        desktopMegaParent?.setAttribute('aria-expanded', 'false');
        resetDesktopMega();
      }, { signal });
    }

    const syncMobileMenuState = () => {
      const isOpen = Boolean(mobileMenu?.open);
      document.documentElement.classList.toggle('kv-mobile-menu-open', isOpen);
      document.body?.classList.toggle('kv-mobile-menu-open', isOpen);

      if (mobileMenu instanceof HTMLDetailsElement) {
        if (isOpen) lockScroll(mobileMenu);
        else unlockScroll(mobileMenu);
      }

      if (mobileSummary instanceof HTMLElement) {
        mobileSummary.setAttribute('aria-expanded', String(isOpen));
      }

      if (mobilePanel instanceof HTMLElement) {
        mobilePanel.setAttribute('aria-hidden', String(!isOpen));
      }

      if (isOpen && mobileClose instanceof HTMLButtonElement) {
        requestAnimationFrame(() => mobileClose.focus({ preventScroll: true }));
      }
    };

    const closeMobileMenu = ({ restoreFocus = true } = {}) => {
      if (mobileMenu) {
        mobileMenu.querySelectorAll('.kv-mobile-panel__shop[open]').forEach((details) => {
          details.open = false;
        });
        mobileMenu.open = false;
      }

      syncMobileMenuState();

      if (restoreFocus && mobileSummary instanceof HTMLElement && mobileSummary.isConnected) {
        requestAnimationFrame(() => mobileSummary.focus({ preventScroll: true }));
      }
    };

    const keepTabInsideMobileMenu = (event) => {
      if (event.key !== 'Tab' || !mobileMenu?.open || !(mobilePanel instanceof HTMLElement)) return;

      const focusable = Array.from(
        mobilePanel.querySelectorAll(
          "a[href], button:enabled, select:enabled, input:enabled, [tabindex]:not([tabindex^='-'])"
        )
      ).filter(
        (element) =>
          element instanceof HTMLElement &&
          !element.closest('[aria-hidden="true"]')
      );

      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (event.shiftKey && (active === first || !mobilePanel.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    mobileMenu?.addEventListener('toggle', syncMobileMenuState, { signal });
    mobileClose?.addEventListener('click', () => closeMobileMenu(), { signal });
    mobileBackdrop?.addEventListener('click', () => closeMobileMenu(), { signal });
    mobilePanel?.addEventListener('keydown', keepTabInsideMobileMenu, { signal });

    mobilePanel?.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest('a[href]')) closeMobileMenu({ restoreFocus: false });
    }, { signal });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && mobileMenu?.open) {
        event.preventDefault();
        closeMobileMenu();
      }
    }, { signal });

    syncMobileMenuState();

    let initRafA = 0;
    let initRafB = 0;

    const finishInitialization = () => {
      cancelAnimationFrame(initRafA);
      cancelAnimationFrame(initRafB);

      initRafA = requestAnimationFrame(() => {
        initRafB = requestAnimationFrame(() => {
          root.classList.remove('kv-header-initializing');
        });
      });
    };

    signal.addEventListener('abort', () => {
      clearDesktopMegaCloseTimer();
      cancelAnimationFrame(initRafA);
      cancelAnimationFrame(initRafB);
      document.documentElement.classList.remove('kv-mobile-menu-open');
      document.body?.classList.remove('kv-mobile-menu-open');
      if (mobileMenu instanceof HTMLDetailsElement) unlockScroll(mobileMenu);
    }, { once: true });

    const scrollY = () => Math.max(window.scrollY || window.pageYOffset || 0, 0);

    const resetHeader = () => {
      header.classList.remove('kv-sticky--fixed', 'kv-sticky--hidden');
      header.classList.add('kv-sticky--visible');
      spacer.style.height = '0px';
    };

    if (mode === 'none') {
      resetHeader();
      finishInitialization();
      return;
    }

    let lastY = scrollY();
    let stickyStart = 0;
    let ticking = false;
    let direction = 0;
    let distance = 0;

    const DOWN_THRESHOLD = 6;
    const UP_THRESHOLD = 3;

    const menuIsOpen = () =>
      Boolean(mobileMenu?.open) ||
      Boolean(desktopMega?.matches(':hover')) ||
      Boolean(desktopMega && desktopMega.contains(document.activeElement));

    const measure = () => {
      stickyStart = sentinel.getBoundingClientRect().top + scrollY();

      if (header.classList.contains('kv-sticky--fixed')) {
        spacer.style.height = `${header.offsetHeight}px`;
      }
    };

    const fixHeader = () => {
      spacer.style.height = `${header.offsetHeight}px`;
      header.classList.add('kv-sticky--fixed');
    };

    const unfixHeader = () => {
      header.classList.remove('kv-sticky--fixed', 'kv-sticky--hidden');
      header.classList.add('kv-sticky--visible');
      spacer.style.height = '0px';
    };

    const showHeader = () => {
      header.classList.remove('kv-sticky--hidden');
      header.classList.add('kv-sticky--visible');
    };

    const hideHeader = () => {
      if (menuIsOpen()) {
        showHeader();
        return;
      }

      header.classList.add('kv-sticky--hidden');
      header.classList.remove('kv-sticky--visible');
    };

    const update = () => {
      ticking = false;
      const currentY = scrollY();

      if (mode === 'always') {
        if (currentY <= stickyStart + 1) {
          unfixHeader();
        } else {
          fixHeader();
          showHeader();
        }

        lastY = currentY;
        return;
      }

      if (currentY <= stickyStart + 1) {
        direction = 0;
        distance = 0;
        unfixHeader();
        lastY = currentY;
        return;
      }

      fixHeader();

      if (menuIsOpen()) {
        direction = 0;
        distance = 0;
        showHeader();
        lastY = currentY;
        return;
      }

      const delta = currentY - lastY;
      lastY = currentY;

      if (Math.abs(delta) < 0.5) return;

      const nextDirection = delta > 0 ? 1 : -1;

      if (nextDirection !== direction) {
        direction = nextDirection;
        distance = 0;
      }

      distance += Math.abs(delta);

      const threshold = direction > 0 ? DOWN_THRESHOLD : UP_THRESHOLD;
      if (distance < threshold) return;

      if (direction > 0) hideHeader();
      else showHeader();

      distance = 0;
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    };

    const onResize = () => {
      window.requestAnimationFrame(() => {
        measure();
        update();
      });
    };

    measure();
    update();
    finishInitialization();

    window.addEventListener('scroll', onScroll, { passive: true, signal });
    window.addEventListener('resize', onResize, { passive: true, signal });

    header.querySelectorAll('details').forEach((details) => {
      details.addEventListener('toggle', () => {
        if (details.open) showHeader();
      }, { signal });
    });
  };

  const initAll = (root = document) => {
    if (root instanceof Element && root.matches('[data-kv-header-section]')) {
      initKervelanHeader(root);
    }

    root.querySelectorAll?.('[data-kv-header-section]').forEach(initKervelanHeader);
  };

  initAll();

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const roots = [];
    if (event.target instanceof Element && event.target.matches('[data-kv-header-section]')) roots.push(event.target);
    event.target.querySelectorAll?.('[data-kv-header-section]').forEach((root) => roots.push(root));
    roots.forEach((root) => root._kvStickyAbort?.abort());
  });

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) initAll();
  });
})();
